import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Alert,
  IconButton, Tooltip, CircularProgress, Chip, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { audioFilesAPI } from '../../services/api';

const AudioVersionsPanel = ({ book, handlePlayAudio }) => {
  const [loading, setLoading] = useState(true);
  const [audioFiles, setAudioFiles] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAudioFiles();
  }, [book?.id]);

  const fetchAudioFiles = async () => {
    if (!book?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await audioFilesAPI.getAllAudioFiles(1, 100, '', '', '', book.id);
      setAudioFiles(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch audio files:", err);
      setError("Failed to load audio files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (audioId) => {
    setSelectedAudioId(audioId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedAudioId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAudioId) return;
    
    try {
      setDeleteLoading(true);
      await audioFilesAPI.deleteAudioFile(selectedAudioId);
      setDeleteDialogOpen(false);
      setSelectedAudioId(null);
      
      // Refresh the audio files list
      fetchAudioFiles();
    } catch (err) {
      console.error("Failed to delete audio file:", err);
      setError("Failed to delete audio file. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb < 0.1 ? '<0.1 MB' : `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Audio Files</Typography>
        <Tooltip title="Refresh audio files">
          <IconButton onClick={fetchAudioFiles} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : audioFiles.length === 0 ? (
        <Alert severity="info">
          No audio versions available. Generate audio from text versions in the Text Versions tab.
        </Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Voice</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audioFiles.map((audio) => (
                <TableRow key={audio.id}>
                  <TableCell>
                    <Chip 
                      label={audio.type === 'short' ? 'Short' : 
                             audio.type === 'medium' ? 'Medium' : 
                             audio.type === 'long' ? 'Long' : 
                             audio.type || 'Original'} 
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={audio.provider || 'Unknown'} 
                      size="small"
                      color={
                        audio.provider === 'openai' ? 'success' :
                        audio.provider === 'google' ? 'info' : 'default'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={audio.voiceName || 'Default'}>
                      <Typography noWrap sx={{ maxWidth: 200 }}>
                        {audio.voiceName ? audio.voiceName.split('-').pop() : 'Default'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatDate(audio.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="Play Audio">
                        <IconButton 
                          color="primary"
                          onClick={() => handlePlayAudio(audio.url, `${book.title} - ${audio.type || 'Original'}`)}
                          size="small"
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Audio">
                        <IconButton 
                          color="error"
                          onClick={() => handleDeleteClick(audio.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Audio File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this audio file? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={16} color="inherit" />}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AudioVersionsPanel;
